/* eslint-disable @typescript-eslint/naming-convention */
import type {
    BomState,
    Picture,
    Style,
    StyleEan,
    StyleOption,
    StyleOptionCollection,
    StyleVariant,
    StyleWash,
    TableStyleFragment,
} from '../gql';

import {
    randBoolean,
    randColor,
    randCompanyName,
    randHex,
    randImg,
    randNumber,
    randRecentDate,
    randSentence,
    randUuid,
    randWord,
} from '@ngneat/falso';

import { mockBrand } from './brand';
import { mockCollection, mockStyleCollection } from './collection';
import { mockUser } from './user';

const mockPicture = (overwrite?: Partial<Picture>): Picture => ({
    url: randImg(),
    isThumbnail: randBoolean(),
    perspectiveName: randWord(),
    perspectiveNumber: randNumber(),
    ...overwrite,
    ['__typename']: 'Picture',
});

const mockStyleEan = (overwrite?: Partial<StyleEan>): StyleEan => ({
    eanNumber: randNumber().toString(),
    lengthName: randWord(),
    lengthNumber: randNumber(),
    sizeName: randWord(),
    sizeNumber: randNumber(),
    sizeSortOrder: randNumber(),
    styleEanNumber: randNumber(),
    ...overwrite,
    ['__typename']: 'StyleEan',
});

const mockStyleOptionCollection = (
    overwrite?: Partial<StyleOptionCollection>
): StyleOptionCollection => ({
    collection: mockStyleCollection(),
    isInactive: false,
    styleOption: undefined, // Set this to undefined to prevent infinite loops when used inside styleOption
    styleOptionCollectionNumber: randNumber(),
    ...overwrite,
    ['__typename']: 'StyleOptionCollection',
});

const mockStyleVariant = (overwrite?: Partial<StyleVariant>): StyleVariant => ({
    isDeleted: false,
    styleVariantName: randWord(),
    styleVariantNumber: randNumber(),
    ...overwrite,
    ['__typename']: 'StyleVariant',
});

const mockStyleOption = (overwrite?: Partial<StyleOption>): StyleOption => ({
    bomState: 'VALID' as BomState,
    isInactive: false,
    mainColourHexCode: randHex(),
    mainColourName: randColor(),
    mainColourNumber: randNumber(),
    masterColourHexCode: randHex(),
    masterColourName: randColor(),
    masterColourNumber: randNumber(),
    pictures: Array.from({ length: randNumber({ min: 0, max: 3 }) }, () =>
        mockPicture()
    ),
    styleEans: Array.from({ length: randNumber({ min: 1, max: 5 }) }, () =>
        mockStyleEan()
    ),
    styleOptionCollections: Array.from(
        { length: randNumber({ min: 1, max: 3 }) },
        () => mockStyleOptionCollection()
    ),
    styleOptionNumber: randNumber(),
    styleVariant: mockStyleVariant(),
    ...overwrite,
    ['__typename']: 'StyleOption',
});

const mockStyleWash = (overwrite?: Partial<StyleWash>): StyleWash => ({
    styleWashNumber: randUuid(),
    washCode: randUuid(),
    washNumber: randUuid(),
    ...overwrite,
    ['__typename']: 'StyleWashes',
});

const mockStyle = (overwrite?: Partial<Style>): Style => {
    const brand = mockBrand();
    const user = mockUser();
    return {
        bodyTypeName: randWord(),
        bodyTypeNumber: randNumber({ min: 10, max: 99 }),
        brandName: brand.brandName,
        brandNumber: brand.brandNumber,
        buyerEmail: user.email,
        buyerName: user.fullName,
        buyerNumber: user.userNumber,
        changedBy: user.userNumber,
        collections: Array.from(
            { length: randNumber({ min: 1, max: 5 }) },
            () => mockStyleCollection({ brandNumber: brand.brandNumber })
        ),
        createdBy: user.userNumber,
        createdDate: randRecentDate(),
        expectedSupplierName: randCompanyName(),
        expectedSupplierNumber: randUuid(),
        fitName: randWord(),
        fitNumber: randNumber({ min: 10, max: 99 }),
        genderName: randWord(),
        genderNumber: randNumber({ min: 10, max: 99 }),
        hasBoms: randBoolean(),
        isMoreSustainableChoice: randBoolean(),
        isNonApparel: false,
        managingSystem: randWord(),
        masterStyleCategoryName: randWord(),
        masterStyleCategoryNumber: randNumber({ min: 10, max: 99 }).toString(),
        masterStyleSubcategoryName: randWord(),
        masterStyleSubcategoryNumber: randNumber({
            min: 10,
            max: 99,
        }).toString(),
        neckName: randWord(),
        neckNumber: randNumber({ min: 10, max: 99 }),
        polyBagged: randBoolean(),
        productLineName: randWord(),
        productLineNumber: randNumber({ min: 10, max: 99 }).toString(),
        purchaseDivisionName: randWord(),
        purchaseDivisionNumber: randNumber({ min: 10, max: 99 }),
        qualityDescription: randSentence(),
        salesDescription: randSentence(),
        salesDescriptionLocked: randBoolean(),
        sleeveName: randWord(),
        sleeveNumber: randNumber({ min: 10, max: 99 }),
        status: randWord(),
        styleDescription: randSentence(),
        styleName: randWord({ length: 3 }).join(' '),
        styleNumber: randNumber({ min: 10000000, max: 99999999 }),
        styleOptions: Array.from(
            { length: randNumber({ min: 1, max: 10 }) },
            () => mockStyleOption()
        ),
        styleWashes: Array.from(
            { length: randNumber({ min: 1, max: 3 }) },
            () => mockStyleWash()
        ),
        ...overwrite,
        ['__typename']: 'Style',
    };
};

const mockTableStyle = (
    override?: Partial<TableStyleFragment>
): TableStyleFragment => {
    const brand = mockBrand();
    const collections = Array.from({ length: randNumber() }).map((x) => {
        const { __typename, collectionTerm } = mockCollection();
        return {
            __typename,
            collectionTerm,
        };
    });
    return {
        styleNumber: randNumber({ min: 10000000, max: 99999999 }),
        styleName: randWord({ length: 3 }).join(' '),
        brandNumber: brand.brandNumber,
        brandName: brand.brandName,
        hasBoms: randBoolean(),
        isNonApparel: false,
        collections,
        ...override,
        ['__typename']: 'Style',
    };
};

export {
    mockPicture,
    mockStyle,
    mockStyleEan,
    mockStyleOption,
    mockStyleOptionCollection,
    mockStyleVariant,
    mockStyleWash,
    mockTableStyle,
};
